import { useState } from 'react';
import { uploadToServer } from '../../services/uploadService';
import { useApp } from '../../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { auth, db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import PhoneVerificationModal from '../common/PhoneVerificationModal';
import { predefinedIcons } from '../../constants/menuIcons';
import {
    FiSmartphone,
    FiGlobe,
    FiAlertTriangle,
    FiCalendar,
    FiEdit,
    FiTrash2,
    FiUpload,
    FiImage,
    FiTag,
    FiGrid,
    FiCheckCircle,
    FiX,
    FiZap,
    // eslint-disable-next-line no-unused-vars
    FiPlus,
    FiSave,
    FiCheck,
    FiDollarSign
} from 'react-icons/fi';
import { FaAndroid, FaApple } from 'react-icons/fa';

const AppsTable = ({
    userApps,
    formatFirestoreTimestamp,
    formatPrice,
    handleDeleteApp,
    deletingAppId,
    updateApp,
    showToast
}) => {
    const { dispatch } = useApp();
    const navigate = useNavigate();
    const { isDarkMode } = useTheme();

    const [editingApp, setEditingApp] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        url: '',
        platforms: [],
        logo: null,
        splash: null,
        icons: []
    });
    const [previewLogo, setPreviewLogo] = useState(null);
    const [previewSplash, setPreviewSplash] = useState(null);
    const [saving, setSaving] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({ logo: 0, splash: 0 });
    const [iconsError, setIconsError] = useState('');
    const [showPhoneModal, setShowPhoneModal] = useState(false);

    // ✅ FUNÇÃO PARA LIMPAR DADOS E IR PARA STEP1
    const handleNewAppWithReset = async () => {
        try {
            const user = auth.currentUser;
            if (user) {
                const userDoc = await getDoc(doc(db, 'users', user.uid));
                if (userDoc.exists()) {
                    const userData = userDoc.data();
                    if (!userData.phone) {
                        setShowPhoneModal(true);
                        return;
                    }
                }
            }
        } catch (error) {
            console.error("Erro ao verificar telefone:", error);
        }

        dispatch({ type: 'RESET_APP_DATA' });
        navigate('/app-creation', { state: { allowNew: true } });
    };

    // ✅ FUNÇÃO CORRIGIDA PARA VERIFICAR SE O APP ESTÁ BLOQUEADO
    const isAppBlocked = (app) => {
        if (!app || !app.status) return false;

        const status = app.status.toString().toLowerCase().trim();

        const blockedStatuses = [
            'pagamento pendente',
            'pending',
            'review',
            'revisao',
            'revisão',
            'development',
            'em desenvolvimento'
        ];

        return blockedStatuses.some(blockedStatus =>
            status.includes(blockedStatus.toLowerCase())
        );
    };

    // ✅ FUNÇÃO PARA OBTER MENSAGEM DO STATUS
    const getStatusMessage = (status) => {
        if (!status) return 'Desconhecido';

        const statusLower = status.toString().toLowerCase();
        switch (statusLower) {
            case 'pending_payment':
            case 'pagamento pendente':
                return 'Aguardando Pagamento';
            case 'pending':
            case 'pendente':
                return 'Pendente';
            case 'review':
            case 'revisao':
            case 'revisão':
                return 'Em Revisão';
            case 'active':
            case 'ativo':
                return 'Ativo';
            case 'development':
            case 'em desenvolvimento':
                return 'Em desenvolvimento';
            case 'rejected':
            case 'rejeitado':
                return 'Rejeitado';
            case 'published':
            case 'publicado':
                return 'Publicado';
            default:
                return status;
        }
    };

    // ✅ FUNÇÃO PARA OBTER CORES DO STATUS
    const getStatusColors = (status) => {
        if (!status) return isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800';

        const statusLower = status.toString().toLowerCase();
        switch (statusLower) {
            case 'pending_payment':
            case 'pagamento pendente':
                return isDarkMode ? 'bg-violet-900/50 text-violet-300' : 'bg-violet-100 text-violet-700';
            case 'pending':
            case 'pendente':
                return isDarkMode ? 'bg-yellow-900/50 text-yellow-300' : 'bg-yellow-100 text-yellow-800';
            case 'review':
            case 'revisao':
            case 'revisão':
                return isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-800';
            case 'active':
            case 'ativo':
                return isDarkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-800';
            case 'development':
            case 'em desenvolvimento':
                return isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-800';
            case 'rejected':
            case 'rejeitado':
                return isDarkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-800';
            case 'published':
            case 'publicado':
                return isDarkMode ? 'bg-indigo-900/50 text-indigo-300' : 'bg-indigo-100 text-indigo-800';
            default:
                return isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800';
        }
    };

    // Função para abrir o modal de edição
    const openEditModal = (app) => {
        if (isAppBlocked(app)) {
            if (showToast) showToast('Este aplicativo está bloqueado para edição. Status atual: ' + getStatusMessage(app.status), 'error');
            else alert('Este aplicativo está bloqueado para edição. Status atual: ' + getStatusMessage(app.status));
            return;
        }

        setEditingApp(app);
        setFormData({
            name: app.name || '',
            url: app.url || '',
            platforms: app.platforms || [],
            logo: null,
            splash: null,
            icons: app.icons || []
        });
        setPreviewLogo(app.logo || null);
        setPreviewSplash(app.splash || null);
        setIconsError('');
        setIsEditModalOpen(true);
    };

    // ✅ FUNÇÃO CORRIGIDA PARA LIDAR COM EXCLUSÃO
    const handleDelete = (appId, appName) => {
        const app = userApps.find(app => app.id === appId);

        if (app && isAppBlocked(app)) {
            if (showToast) showToast('Este aplicativo está bloqueado para exclusão. Status atual: ' + getStatusMessage(app.status), 'error');
            else alert('Este aplicativo está bloqueado para exclusão. Status atual: ' + getStatusMessage(app.status));
            return;
        }

        handleDeleteApp(appId, appName);
    };

    // Função para fechar o modal
    const closeEditModal = () => {
        setIsEditModalOpen(false);
        setEditingApp(null);
        setFormData({
            name: '',
            url: '',
            platforms: [],
            logo: null,
            splash: null,
            icons: []
        });
        setPreviewLogo(null);
        setPreviewSplash(null);
        setSaving(false);
        setUploadProgress({ logo: 0, splash: 0 });
        setIconsError('');
    };

    // Função para lidar com mudanças nos inputs
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Função para lidar com seleção de plataformas
    const handlePlatformChange = (platform) => {
        setFormData(prev => ({
            ...prev,
            platforms: prev.platforms.includes(platform)
                ? prev.platforms.filter(p => p !== platform)
                : [...prev.platforms, platform]
        }));
    };

    // Função para lidar com upload de imagens
    const handleImageUpload = (e, type) => {
        const file = e.target.files[0];
        if (file) {
            if (type === 'logo') {
                setFormData(prev => ({ ...prev, logo: file }));
                setPreviewLogo(URL.createObjectURL(file));
            } else if (type === 'splash') {
                setFormData(prev => ({ ...prev, splash: file }));
                setPreviewSplash(URL.createObjectURL(file));
            }
        }
    };

    // ✅ FUNÇÃO CORRIGIDA PARA ADICIONAR/REMOVER ÍCONES DO MENU COM VALIDAÇÃO
    const handleIconChange = (icon, action) => {
        setIconsError(''); // Limpar erro ao modificar seleção

        if (action === 'add') {
            // Verificar se já atingiu o máximo
            if (formData.icons.length >= 5) {
                setIconsError('Máximo de 5 ícones permitidos');
                return;
            }
            setFormData(prev => ({
                ...prev,
                icons: [...prev.icons, icon]
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                icons: prev.icons.filter(i => i.id !== icon.id)
            }));
        }
    };

    // ✅ VALIDAR FORMULÁRIO ANTES DE SALVAR
    const validateForm = () => {
        // Validar ícones do menu
        if (formData.icons.length < 2) {
            setIconsError('Selecione no mínimo 2 ícones para o menu');
            return false;
        }
        if (formData.icons.length > 5) {
            setIconsError('Selecione no máximo 5 ícones para o menu');
            return false;
        }

        // Validar plataformas
        if (formData.platforms.length === 0) {
            if (showToast) showToast('Selecione pelo menos uma plataforma', 'error');
            else alert('Selecione pelo menos uma plataforma');
            return false;
        }

        // Validar campos obrigatórios
        if (!formData.name.trim() || !formData.url.trim()) {
            if (showToast) showToast('Preencha todos os campos obrigatórios', 'error');
            else alert('Preencha todos os campos obrigatórios');
            return false;
        }

        return true;
    };

    // ✅ FUNÇÃO CORRIGIDA PARA SALVAR ALTERAÇÕES
    const handleSaveChanges = async () => {
        if (!editingApp) return;

        // Validar formulário antes de salvar
        if (!validateForm()) {
            return;
        }

        setSaving(true);
        setUploadProgress({ logo: 0, splash: 0 });

        try {
            let logoUrl = editingApp.logo;
            let splashUrl = editingApp.splash;

            // ✅ FAZER UPLOAD DAS NOVAS IMAGENS SE FORAM SELECIONADAS
            if (formData.logo instanceof File) {
                setUploadProgress(prev => ({ ...prev, logo: 50 }));
                logoUrl = await uploadToServer(formData.logo, 'logos');
                setUploadProgress(prev => ({ ...prev, logo: 100 }));
            }

            if (formData.splash instanceof File) {
                setUploadProgress(prev => ({ ...prev, splash: 50 }));
                splashUrl = await uploadToServer(formData.splash, 'splashs');
                setUploadProgress(prev => ({ ...prev, splash: 100 }));
            }

            // Preparar dados para atualização
            const updateData = {
                name: formData.name,
                url: formData.url,
                platforms: formData.platforms,
                icons: formData.icons.map(i => ({
                    id: i.id,
                    name: i.name,
                    url: i.url || '',
                    price: i.price || 0
                })),
                logo: logoUrl,
                splash: splashUrl,
                // ✅ MANTÉM O PREÇO ORIGINAL - NÃO ALTERA COM PLATAFORMAS
                totalPrice: editingApp.totalPrice
            };

            console.log('Atualizando app com dados:', updateData);

            // ✅ CHAMAR A FUNÇÃO updateApp DO COMPONENTE PAI
            await updateApp(editingApp.id, updateData);

            if (showToast) showToast('App atualizado com sucesso!', 'success');
            else alert('App atualizado com sucesso!');
            closeEditModal();

        } catch (error) {
            console.error('Erro ao atualizar app:', error);
            if (showToast) showToast(`Erro ao atualizar app: ${error.message}`, 'error');
            else alert(`Erro ao atualizar app: ${error.message}`);
        } finally {
            setSaving(false);
            setUploadProgress({ logo: 0, splash: 0 });
        }
    };

    return (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-none shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-none border border-gray-100 dark:border-gray-700 overflow-hidden">
            {userApps.length > 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-none border border-gray-100 dark:border-gray-700 overflow-hidden">
                    <div className="overflow-x-auto w-full custom-scrollbar">
                        <table className="min-w-full divide-y divide-gray-100 dark:divide-gray-700">
                        <thead className="bg-gray-50/50 dark:bg-gray-900/50">
                            <tr>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    Nome
                                </th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                                    Plataformas
                                </th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">
                                    Valor
                                </th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                                    Data
                                </th>
                                <th className="px-4 py-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    Ações
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-50 dark:divide-gray-700/50">
                            {userApps.map(app => {
                                const blocked = isAppBlocked(app);
                                return (
                                    <tr key={app.id} className={`${isDarkMode ? 'hover:bg-gray-750' : 'hover:bg-gray-50'} transition-colors duration-200`}>
                                        <td className="px-4 py-4 align-middle">
                                            <div className="flex items-center min-w-0">
                                                <div className="flex-shrink-0 h-12 w-12 bg-gray-50 dark:bg-gray-700 rounded-none flex items-center justify-center mr-4 border border-gray-100 dark:border-gray-600">
                                                    {app.logo ? (
                                                        <img src={app.logo} alt={app.name} className="h-8 w-8 rounded-none object-cover" />
                                                    ) : (
                                                        <FiSmartphone className="text-xl text-gray-400" />
                                                    )}
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                                                        {app.name}
                                                    </div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate flex items-center gap-1 mt-1 font-light">
                                                        <FiGlobe className="w-3.5 h-3.5" />
                                                        <span className="truncate">{app.url}</span>
                                                    </div>
                                                    {blocked && (
                                                        <div className="text-xs text-violet-600 mt-1 flex items-center gap-1 font-medium">
                                                            <FiAlertTriangle className="w-3.5 h-3.5" />
                                                            <span className="hidden xs:inline">Bloqueado para edição</span>
                                                            <span className="xs:hidden">Bloqueado</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 align-middle whitespace-nowrap">
                                            <span className={`px-2 py-1 sm:px-3 sm:py-1.5 inline-flex text-xs leading-4 font-semibold rounded-full ${getStatusColors(app.status)} shadow-sm`}>
                                                {getStatusMessage(app.status)}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 align-middle whitespace-nowrap hidden sm:table-cell">
                                            <div className="flex flex-wrap gap-2">
                                                {app.platforms && app.platforms.map((platform, index) => (
                                                    <span key={index} className="px-2.5 py-1 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-none text-xs font-medium whitespace-nowrap flex items-center gap-1.5">
                                                        {platform === 'android' ? (<FaAndroid className="w-4 h-4 text-green-500" title="Android" />) : (<FaApple className="w-4 h-4 text-gray-800 dark:text-gray-200" title="iOS" />)}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 align-middle whitespace-nowrap text-sm font-medium hidden md:table-cell text-gray-900 dark:text-white">
                                            <span>
                                                R$ {new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(app.totalPrice || 0)}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 align-middle whitespace-nowrap text-xs hidden lg:table-cell text-gray-500 dark:text-gray-400 font-light">
                                            <div className="flex flex-row items-center">
                                                <FiCalendar className="w-3.5 h-3.5 shrink-0 mr-1.5" />
                                                <span className="mt-[2px] leading-none">{formatFirestoreTimestamp(app.createdAt)}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 align-middle whitespace-nowrap">
                                            <div className="flex flex-row space-x-2 min-w-[80px] justify-center">
                                                <button
                                                    onClick={() => openEditModal(app)}
                                                    disabled={blocked || deletingAppId === app.id}
                                                    className={`px-3 py-2 rounded-none text-xs font-medium transition-all duration-300 ease-in-out flex items-center justify-center border ${
                                                        blocked || deletingAppId === app.id
                                                            ? 'text-gray-400 border-gray-200 bg-gray-50 dark:bg-gray-800 dark:border-gray-700 cursor-not-allowed opacity-50'
                                                            : 'text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                                                    }`}
                                                    title={blocked ? 'App bloqueado para edição' : 'Editar app'}
                                                >
                                                    <FiEdit className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(app.id, app.name)}
                                                    disabled={deletingAppId === app.id || blocked}
                                                    className={`px-3 py-2 rounded-none text-xs font-medium transition-all duration-300 ease-in-out flex items-center justify-center border ${
                                                        deletingAppId === app.id || blocked
                                                            ? 'text-gray-400 border-gray-200 bg-gray-50 dark:bg-gray-800 dark:border-gray-700 cursor-not-allowed opacity-50'
                                                            : 'text-red-600 dark:text-red-400 border-red-100 dark:border-red-900/30 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 hover:border-red-200 dark:hover:border-red-800/50'
                                                    }`}
                                                    title={blocked ? 'App bloqueado para exclusão' : 'Excluir app'}
                                                >
                                                    {deletingAppId === app.id ? <span className="w-4 h-4 flex items-center justify-center">...</span> : <FiTrash2 className="w-4 h-4" />}
                                                </button>
                                            </div>
                                        </td>

                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                  </div>
                </div>
            ) : (
                <div className="text-center py-8 sm:py-12 md:py-16">
                    <div className="mx-auto h-16 w-16 sm:h-20 sm:w-20 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-none flex items-center justify-center mb-4 shadow-lg">
                        <FiSmartphone className="text-xl sm:text-2xl text-gray-600 dark:text-gray-400" />
                    </div>
                    <h3 className={`text-base sm:text-lg font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Nenhum app criado ainda
                    </h3>
                    <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'} text-xs sm:text-sm md:text-base mb-6 max-w-md mx-auto px-4`}>
                        Comece criando seu primeiro aplicativo para ver ele listado aqui.
                    </p>
                    <button
                        onClick={handleNewAppWithReset}
                        className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-none text-xs sm:text-sm font-medium hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                    >
                        <span className="flex items-center gap-2">
                            <FiZap className="w-4 h-4" />
                            <span>Criar Primeiro App</span>
                        </span>
                    </button>
                </div>
            )}

            {/* Modal de Edição - Layout Modernizado */}
            {isEditModalOpen && editingApp && (
                <div className="fixed inset-0 bg-gray-900/40 flex items-center justify-center p-2 sm:p-4 z-50 backdrop-blur-md">
                    <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white/95 backdrop-blur-sm'} rounded-none sm:rounded-none shadow-2xl shadow-blue-500/10 w-full max-w-[95vw] sm:max-w-4xl max-h-[90vh] overflow-y-auto border ${isDarkMode ? 'border-gray-700/50' : 'border-white/20'} mx-2 scrollbar-hidden`}>
                        <style>{`
                            .scrollbar-hidden::-webkit-scrollbar {
                                display: none;
                            }
                            .scrollbar-hidden {
                                -ms-overflow-style: none;
                                scrollbar-width: none;
                            }
                        `}</style>
                        <div className="p-5 sm:p-8 md:p-10">
                            {/* Header */}
                            <div className="flex justify-between items-start mb-6 sm:mb-8">
                                <div>
                                    <h3 className={`text-2xl sm:text-3xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r ${isDarkMode ? 'from-blue-400 to-indigo-400' : 'from-blue-600 to-indigo-600'} tracking-tight`}>
                                        Editar App
                                    </h3>
                                    <p className={`text-sm sm:text-base ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mt-1.5 font-medium`}>
                                        Faça as alterações necessárias no seu aplicativo
                                    </p>
                                </div>
                                <button
                                    onClick={closeEditModal}
                                    className={`p-2 rounded-full transition-all duration-300 ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-400 hover:text-gray-900 hover:bg-gray-100'} hover:rotate-90`}
                                    disabled={saving}
                                >
                                    <FiX className="w-5 h-5 sm:w-6 sm:h-6" />
                                </button>
                            </div>

                            {/* Indicador de Progresso de Upload */}
                            {(uploadProgress.logo > 0 || uploadProgress.splash > 0) && (
                                <div className={`mb-4 sm:mb-6 ${isDarkMode ? 'bg-blue-900/30 border border-blue-800' : 'bg-blue-50 border border-blue-200'} p-3 sm:p-4 rounded-none`}>
                                    <h4 className={`font-semibold ${isDarkMode ? 'text-blue-300' : 'text-blue-800'} mb-3 text-sm md:text-base flex items-center gap-2`}>
                                        <FiUpload className="w-4 h-4" />
                                        <span>Upload em progresso...</span>
                                    </h4>
                                    {uploadProgress.logo > 0 && (
                                        <div className="mb-3">
                                            <div className={`flex justify-between text-xs sm:text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'} mb-2`}>
                                                <span className="flex items-center gap-2">
                                                    <FiImage className="w-4 h-4" />
                                                    <span>Logo</span>
                                                </span>
                                                <span>{uploadProgress.logo}%</span>
                                            </div>
                                            <div className={`w-full ${isDarkMode ? 'bg-blue-800' : 'bg-blue-200'} rounded-full h-2`}>
                                                <div
                                                    className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-300"
                                                    style={{ width: `${uploadProgress.logo}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    )}
                                    {uploadProgress.splash > 0 && (
                                        <div>
                                            <div className={`flex justify-between text-xs sm:text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'} mb-2`}>
                                                <span className="flex items-center gap-2">
                                                    <FiImage className="w-4 h-4" />
                                                    <span>Splash Screen</span>
                                                </span>
                                                <span>{uploadProgress.splash}%</span>
                                            </div>
                                            <div className={`w-full ${isDarkMode ? 'bg-blue-800' : 'bg-blue-200'} rounded-full h-2`}>
                                                <div
                                                    className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-300"
                                                    style={{ width: `${uploadProgress.splash}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="space-y-4 sm:space-y-6 md:space-y-8">
                                {/* Informações Básicas */}
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-8">
                                    {/* Nome do App */}
                                    <div className="space-y-2">
                                        <label className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} flex items-center gap-2`}>
                                            <FiTag className="w-4 h-4 text-blue-500" />
                                            <span>Nome do App</span>
                                            <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            className={`w-full px-4 py-3.5 border ${isDarkMode ? 'bg-gray-700/50 border-gray-600 text-white focus:border-blue-500 focus:bg-gray-700' : 'bg-gray-50/50 border-gray-200 text-gray-900 focus:bg-white focus:border-blue-500'} rounded-none focus:outline-none focus:ring-4 focus:ring-blue-500/10 text-sm font-medium transition-all duration-300`}
                                            required
                                            disabled={saving}
                                            placeholder="Digite o nome do app"
                                        />
                                    </div>

                                    {/* URL do App */}
                                    <div className="space-y-2">
                                        <label className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} flex items-center gap-2`}>
                                            <FiGlobe className="w-4 h-4 text-blue-500" />
                                            <span>URL do App</span>
                                            <span className="text-red-500">*</span>
                                        </label>
                                        <div className="flex items-center">
                                            <input
                                                type="text"
                                                name="url"
                                                value={formData.url}
                                                onChange={handleInputChange}
                                                className={`w-full px-4 py-3.5 border ${isDarkMode ? 'bg-gray-700/50 border-gray-600 text-white focus:border-blue-500 focus:bg-gray-700' : 'bg-gray-50/50 border-gray-200 text-gray-900 focus:bg-white focus:border-blue-500'} rounded-none focus:outline-none focus:ring-4 focus:ring-blue-500/10 text-sm font-medium transition-all duration-300`}
                                                required
                                                disabled={saving}
                                                placeholder="sua-url"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Plataformas (Bloqueado para Edição) */}
                                <div className="space-y-3">
                                    <div className="flex flex-col gap-1">
                                        <label className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} flex items-center gap-2`}>
                                            <FiSmartphone className="w-4 h-4 text-indigo-500" />
                                            <span>Plataformas</span>
                                            <span className="text-red-500">*</span>
                                        </label>
                                        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                            As plataformas não podem ser alteradas após o pagamento. Para alterações, <a href="https://wa.me/5582996992182" target="_blank" rel="noreferrer" className="text-blue-500 hover:underline">fale com o suporte</a>.
                                        </p>
                                    </div>
                                    <div className="flex flex-wrap gap-3 sm:gap-4">
                                        {['android', 'ios'].map(platform => {
                                            const isSelected = formData.platforms.includes(platform);
                                            return (
                                                <button
                                                    key={platform}
                                                    type="button"
                                                    disabled={true}
                                                    className={`relative overflow-hidden flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-none text-sm font-bold transition-all duration-300 group cursor-not-allowed opacity-80 ${
                                                        isSelected
                                                            ? isDarkMode
                                                                ? 'bg-blue-900/40 text-blue-300 ring-2 ring-blue-500 ring-offset-2 ring-offset-gray-800 shadow-lg shadow-blue-900/20'
                                                                : 'bg-blue-50 text-blue-700 ring-2 ring-blue-500 ring-offset-2 ring-offset-white shadow-lg shadow-blue-500/20'
                                                            : isDarkMode
                                                                ? 'bg-gray-800 text-gray-400 hover:bg-gray-700 border border-gray-700'
                                                                : 'bg-white text-gray-500 hover:bg-gray-50 border border-gray-200 hover:border-gray-300'
                                                    }`}
                                                >
                                                    {platform === 'android' ? <FaAndroid className="w-5 h-5" /> : <FaApple className="w-5 h-5 mb-0.5" />}
                                                    <span className="capitalize">{platform}</span>
                                                    {isSelected && (
                                                        <span className="absolute top-1.5 right-1.5">
                                                            <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>
                                                        </span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Imagens */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-8">
                                    {/* Logo do App */}
                                    <div className="space-y-3">
                                        <label className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} flex items-center gap-2`}>
                                            <FiImage className="w-4 h-4 text-blue-500" />
                                            <span>Logo do App</span>
                                        </label>
                                        <label className={`relative flex flex-col items-center justify-center w-full h-32 sm:h-40 rounded-none border-2 border-dashed transition-all duration-300 group cursor-pointer overflow-hidden ${isDarkMode ? 'border-gray-600 hover:border-blue-500 bg-gray-800/50' : 'border-gray-300 hover:border-blue-500 bg-gray-50 hover:bg-blue-50/50'} ${(previewLogo || editingApp.logo) ? 'border-transparent bg-white dark:bg-gray-800' : ''}`}>
                                            {(previewLogo || editingApp.logo) ? (
                                                <>
                                                    <img src={previewLogo || editingApp.logo} alt="Logo" className="absolute inset-0 w-full h-full object-contain p-2" />
                                                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center backdrop-blur-sm">
                                                        <span className="text-white text-sm font-medium flex items-center gap-2">
                                                            <FiEdit className="w-4 h-4" /> Alterar
                                                        </span>
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="flex flex-col items-center justify-center text-center p-4">
                                                    <div className={`p-3 rounded-full mb-3 ${isDarkMode ? 'bg-gray-700' : 'bg-white shadow-sm transition-transform group-hover:-translate-y-1'}`}>
                                                        <FiUpload className={`w-6 h-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-400 group-hover:text-blue-500'}`} />
                                                    </div>
                                                    <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Clique para enviar Logo</p>
                                                    <p className="text-xs text-gray-500 mt-1">PNG ou JPG</p>
                                                </div>
                                            )}
                                            <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'logo')} className="hidden" disabled={saving} />
                                        </label>
                                    </div>

                                    {/* Splash Screen */}
                                    <div className="space-y-3">
                                        <label className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} flex items-center gap-2`}>
                                            <FiSmartphone className="w-4 h-4 text-purple-500" />
                                            <span>Tela de Splash</span>
                                        </label>
                                        <label className={`relative flex flex-col items-center justify-center w-full h-32 sm:h-40 rounded-none border-2 border-dashed transition-all duration-300 group cursor-pointer overflow-hidden ${isDarkMode ? 'border-gray-600 hover:border-purple-500 bg-gray-800/50' : 'border-gray-300 hover:border-purple-500 bg-gray-50 hover:bg-purple-50/50'} ${(previewSplash || editingApp.splash) ? 'border-transparent bg-black' : ''}`}>
                                            {(previewSplash || editingApp.splash) ? (
                                                <>
                                                    <img src={previewSplash || editingApp.splash} alt="Splash" className="absolute inset-0 w-full h-full object-cover" />
                                                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center backdrop-blur-sm">
                                                        <span className="text-white text-sm font-medium flex items-center gap-2">
                                                            <FiEdit className="w-4 h-4" /> Alterar
                                                        </span>
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="flex flex-col items-center justify-center text-center p-4">
                                                    <div className={`p-3 rounded-full mb-3 ${isDarkMode ? 'bg-gray-700' : 'bg-white shadow-sm transition-transform group-hover:-translate-y-1'}`}>
                                                        <FiUpload className={`w-6 h-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-400 group-hover:text-purple-500'}`} />
                                                    </div>
                                                    <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Clique para enviar Splash</p>
                                                    <p className="text-xs text-gray-500 mt-1">Formato vertical</p>
                                                </div>
                                            )}
                                            <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'splash')} className="hidden" disabled={saving} />
                                        </label>
                                    </div>
                                </div>

                                {/* Ícones do Menu */}
                                <div className="space-y-4">
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                        <label className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} flex items-center gap-2`}>
                                            <FiGrid className="w-4 h-4 text-emerald-500" />
                                            <span>Ícones do Menu</span>
                                            <span className="text-red-500">*</span>
                                        </label>
                                        <div className="flex items-center gap-3">
                                            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${formData.icons.length >= 2 && formData.icons.length <= 5 ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'}`}>
                                                {formData.icons.length} de 5 selecionados (Mín: 2)
                                            </span>
                                            {iconsError && (
                                                <span className="text-xs text-red-500 font-medium flex items-center gap-1 animate-pulse">
                                                    <FiAlertTriangle className="w-3.5 h-3.5" />
                                                    {iconsError}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
                                        {predefinedIcons.map(icon => {
                                            const IconComponent = icon.icon;
                                            const isSelected = formData.icons.some(i => i.id === icon.id);
                                            return (
                                                <button
                                                    type="button"
                                                    key={icon.id}
                                                    className={`relative flex flex-col items-center justify-center p-3 rounded-none cursor-pointer transition-all duration-300 group overflow-hidden ${isSelected
                                                        ? isDarkMode
                                                            ? 'bg-blue-900/20 ring-2 ring-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.15)]'
                                                            : 'bg-blue-50 ring-2 ring-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.15)]'
                                                        : isDarkMode
                                                            ? 'bg-gray-800/50 hover:bg-gray-700/80 border border-gray-700'
                                                            : 'bg-white hover:bg-gray-50 border border-gray-200'
                                                        } ${formData.icons.length >= 5 && !isSelected ? 'opacity-50 grayscale cursor-not-allowed' : ''}`}
                                                    onClick={() => {
                                                        if (formData.icons.length >= 5 && !isSelected) {
                                                            setIconsError('Máximo de 5 ícones permitidos');
                                                            return;
                                                        }
                                                        handleIconChange(icon, isSelected ? 'remove' : 'add');
                                                    }}
                                                >
                                                    {isSelected && (
                                                        <div className="absolute -top-1 -right-1">
                                                            <div className="bg-blue-500 text-white rounded-full p-0.5 shadow-sm">
                                                                <FiCheck className="w-3 h-3" />
                                                            </div>
                                                        </div>
                                                    )}
                                                    <IconComponent className={`w-6 h-6 mb-2 transition-colors ${isSelected ? 'text-blue-500' : 'text-gray-500'}`} />
                                                    <p className={`text-[10px] font-semibold truncate w-full text-center ${isSelected ? (isDarkMode ? 'text-blue-300' : 'text-blue-700') : (isDarkMode ? 'text-gray-400' : 'text-gray-600')}`}>{icon.name}</p>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Resumo e Ações */}
                                <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-700/50 flex flex-col md:flex-row items-center justify-between gap-6">
                                    <div className="w-full md:w-auto">
                                        <div className={`flex items-center gap-4 px-5 py-3.5 rounded-none ${isDarkMode ? 'bg-gradient-to-r from-gray-800 to-gray-800/50 border border-gray-700/50' : 'bg-gradient-to-r from-indigo-50/50 to-blue-50/50 border border-blue-100/50'}`}>
                                            <div className={`p-2.5 rounded-none ${isDarkMode ? 'bg-gray-700' : 'bg-white shadow-sm'}`}>
                                                <FiDollarSign className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-blue-600'}`} />
                                            </div>
                                            <div>
                                                <p className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Preço Atual do App</p>
                                                <p className={`text-lg font-bold tracking-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                    {formatPrice(editingApp.totalPrice)}
                                                </p>
                                            </div>
                                            <div className="ml-4 pl-4 border-l border-gray-200 dark:border-gray-700 hidden sm:block">
                                                <p className="text-[10px] uppercase tracking-wider text-green-600 font-bold flex items-center gap-1.5 mb-0.5">
                                                    <FiCheckCircle className="w-3 h-3" /> Mantido
                                                </p>
                                                <p className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'} max-w-[120px] leading-tight`}>
                                                    Edições não alteram o valor original.
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3 w-full md:w-auto">
                                        <button
                                            onClick={closeEditModal}
                                            className={`flex-1 md:flex-none px-6 py-3.5 rounded-none text-sm font-semibold transition-all duration-300 ${isDarkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-800' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'} w-full sm:w-auto`}
                                            disabled={saving}
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            onClick={handleSaveChanges}
                                            disabled={saving}
                                            className="flex-1 md:flex-none relative overflow-hidden group px-8 py-3.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-none text-sm font-bold shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:hover:transform-none disabled:hover:shadow-blue-500/30 w-full sm:w-auto flex items-center justify-center gap-2"
                                        >
                                            <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-in-out"></div>
                                            {saving ? (
                                                <>
                                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                                    <span className="relative">Salvando...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <FiSave className="w-4 h-4 relative" />
                                                    <span className="relative">Salvar Alterações</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Verificação de Telefone */}
            <PhoneVerificationModal
                isOpen={showPhoneModal}
                onClose={() => setShowPhoneModal(false)}
                onSuccess={() => {
                    setShowPhoneModal(false);
                    dispatch({ type: 'RESET_APP_DATA' });
                    navigate('/app-creation', { state: { allowNew: true } });
                }}
            />
        </div>
    );
};

export default AppsTable;